/* eslint-disable unicode-bom */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import StatCards from '../components/StatCards';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  createCategory,
  getAllCategories,
  getAllSubcategories,
  updateCategory,
  deleteCategory,
} from '../services/api';
import './admin/Category.css';

var NAME_LIMIT = 50;
var DESC_LIMIT = 120;
var ITEMS_PER_PAGE = 8;
var MAX_IMAGE_BYTES = 3.5 * 1024 * 1024; // 3.5 MB (base64 encoding adds ~33%, staying under the 5MB server JSON limit)

var IMAGE_BASE_URL = (
  process.env.VITE_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_IMAGE_URL ||
  'https://api.ishmaijewellery.com'
).replace(/\/+$/, '');

var resolveImageUrl = function (url) {
  if (!url || typeof url !== 'string' || !url.trim()) return '';
  var u = url.trim();
  if (u.startsWith('blob:')) return u;
  if (u.startsWith('data:')) return u;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  u = u.replace(/\\/g, '/');
  var uploadsIndex = u.toLowerCase().indexOf('/uploads/');
  if (uploadsIndex >= 0) u = u.slice(uploadsIndex + 1);
  u = u.replace(/^public\//, '');
  if (u.indexOf('/') === -1 && /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(u)) {
    u = 'uploads/' + u;
  }
  return IMAGE_BASE_URL + '/' + u.replace(/^\/+/, '');
};

var withImageCacheBust = function (url) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return url;
  var sep = url.indexOf('?') === -1 ? '?' : '&';
  return url + sep + 'v=' + Date.now();
};
var pickImageValue = function (value) {
  if (!value) return '';
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') {
    var nestedFields = ['url', 'src', 'path', 'image', 'imageUrl', 'imagePath', 'filename', 'fileName', 'location', 'key'];
    for (var i = 0; i < nestedFields.length; i++) {
      var picked = pickImageValue(value[nestedFields[i]]);
      if (picked) return picked;
    }
  }
  return '';
};

var extractImageSrc = function (obj) {
  if (!obj || typeof obj !== 'object') return '';
  var strFields = ['image', 'imageUrl', 'image_url', 'imageURL', 'imagePath', 'image_path',
                   'categoryImage', 'category_image', 'categoryIcon', 'icon', 'img',
                   'photo', 'thumbnail', 'picture', 'cover', 'avatar', 'banner', 'url',
                   'path', 'file', 'filename', 'fileName'];
  for (var i = 0; i < strFields.length; i++) {
    var v = pickImageValue(obj[strFields[i]]);
    if (v) return resolveImageUrl(v);
  }
  var arrFields = ['images', 'photos', 'pictures', 'thumbnails'];
  for (var j = 0; j < arrFields.length; j++) {
    var arr = obj[arrFields[j]];
    if (Array.isArray(arr) && arr.length > 0) {
      var first = pickImageValue(arr[0]);
      if (first) return resolveImageUrl(first);
    }
  }
  return '';
};

var extractImageRaw = function (obj) {
  if (!obj || typeof obj !== 'object') return '';
  var strFields = ['image', 'imageUrl', 'image_url', 'imageURL', 'imagePath', 'image_path',
                   'categoryImage', 'category_image', 'categoryIcon', 'icon', 'img',
                   'photo', 'thumbnail', 'picture', 'cover', 'avatar', 'banner', 'url',
                   'path', 'file', 'filename', 'fileName'];
  for (var i = 0; i < strFields.length; i++) {
    var v = pickImageValue(obj[strFields[i]]);
    if (v) return v;
  }
  var arrFields = ['images', 'photos', 'pictures', 'thumbnails'];
  for (var j = 0; j < arrFields.length; j++) {
    var arr = obj[arrFields[j]];
    if (Array.isArray(arr) && arr.length > 0) {
      var first = pickImageValue(arr[0]);
      if (first) return first;
    }
  }
  return '';
};

// -- Order helper: sorts categories by numeric `order` field (ascending).
// Categories without an `order` value are treated as 0 and keep their
// original relative position among other unordered items. --
var sortByOrder = function (list) {
  return list.slice().sort(function (a, b) {
    var ao = (a && a.order !== undefined && a.order !== null) ? Number(a.order) : 0;
    var bo = (b && b.order !== undefined && b.order !== null) ? Number(b.order) : 0;
    return ao - bo;
  });
};

var isActiveStatus = function (item) {
  return String((item && item.status) || 'active').toLowerCase() === 'active';
};

var countNestedSubcategories = function (list) {
  return (Array.isArray(list) ? list : []).reduce(function (sum, cat) {
    var subs = cat && (cat.subcategories || cat.subCategories || cat.children);
    return sum + (Array.isArray(subs) ? subs.length : 0);
  }, 0);
};

var extractList = function (res) {
  var root = res && res.data;
  if (Array.isArray(root)) return root;
  if (root && Array.isArray(root.data)) return root.data;
  if (root && Array.isArray(root.categories)) return root.categories;
  if (root && Array.isArray(root.subcategories)) return root.subcategories;
  if (root && Array.isArray(root.items)) return root.items;
  return [];
};

var getCategoryDate = function (category) {
  return category && (category.createdAt || category.created_at || category.date || category.updatedAt || category.updated_at);
};

var dateInputValue = function (value) {
  if (!value) return '';
  var date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
};

// -- Icons --
var EyeIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};

var PencilIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};

var TrashIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6M14 11v6' }),
    React.createElement('path', { d: 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' })
  );
};

var UploadIcon = function () {
  return React.createElement('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
    React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
    React.createElement('polyline', { points: '21 15 16 10 5 21' })
  );
};

var XIcon = function () {
  return React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};

// -- Image Size Warning Modal (same style as Products.js) --
var ImageSizeWarningModal = function (props) {
  var onClose = props.onClose;
  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
      padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 440,
        borderRadius: 20,
        background: '#ffffff',
        border: '2px solid #fb923c',
        boxShadow: '0 32px 80px rgba(249,115,22,0.28)',
        padding: '44px 36px 36px',
        textAlign: 'center',
      }
    },
      // Warning icon circle
      React.createElement('div', {
        style: {
          width: 76, height: 76, borderRadius: '50%',
          margin: '0 auto 24px',
          background: '#fff7ed',
          border: '3px solid #fb923c',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }
      },
        React.createElement('svg', {
          width: 38, height: 38, viewBox: '0 0 24 24',
          fill: 'none', stroke: '#f97316', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', {
        style: { color: '#c2410c', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" }
      }, 'Image Size Too Large!'),
      React.createElement('p', {
        style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 }
      },
        'Please upload an image of size\u00A0',
        React.createElement('span', { style: { color: '#c2410c' } }, '3.5\u00A0MB or below'), '.'
      ),
      React.createElement('p', {
        style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" }
      }, 'Your file exceeds the 3.5\u00A0MB limit. Compress it using TinyPNG or Squoosh and try again.'),
      React.createElement('button', {
        onClick: onClose,
        style: {
          padding: '13px 52px', borderRadius: 12,
          border: 'none', background: '#f97316',
          color: '#fff', fontWeight: 700, fontSize: 15,
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
          boxShadow: '0 8px 24px rgba(249,115,22,0.28)',
        }
      }, 'OK, Got it')
    )
  );
};

var ImageTypeWarningModal = function (props) {
  var onClose = props.onClose;
  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
      padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 440,
        borderRadius: 20,
        background: '#ffffff',
        border: '2px solid #fb923c',
        boxShadow: '0 32px 80px rgba(249,115,22,0.28)',
        padding: '44px 36px 36px',
        textAlign: 'center',
      }
    },
      // Warning icon circle
      React.createElement('div', {
        style: {
          width: 76, height: 76, borderRadius: '50%',
          margin: '0 auto 24px',
          background: '#fff7ed',
          border: '3px solid #fb923c',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }
      },
        React.createElement('svg', {
          width: 38, height: 38, viewBox: '0 0 24 24',
          fill: 'none', stroke: '#f97316', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', {
        style: { color: '#c2410c', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" }
      }, 'Invalid File Type!'),
      React.createElement('p', {
        style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 }
      },
        'Please upload an image in\u00A0',
        React.createElement('span', { style: { color: '#c2410c' } }, 'JPG, PNG, or WEBP format'), '.'
      ),
      React.createElement('p', {
        style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" }
      }, 'Only JPG, PNG, and WEBP files are supported. Convert your image and try again.'),
      React.createElement('button', {
        onClick: onClose,
        style: {
          padding: '13px 52px', borderRadius: 12,
          border: 'none', background: '#f97316',
          color: '#fff', fontWeight: 700, fontSize: 15,
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
          boxShadow: '0 8px 24px rgba(249,115,22,0.28)',
        }
      }, 'OK, Got it')
    )
  );
};

// -- Custom Select (matches theme, no native browser highlight) -------------
var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selected = options.find(function (o) { return o.value === value; });

  return React.createElement('div', { ref: ref, className: 'custom-select', style: { position: 'relative' } },
    React.createElement('button', {
      type: 'button',
      className: 'select-trigger',
      onClick: function () { setOpen(!open); },
      'aria-haspopup': 'listbox',
      'aria-expanded': open,
      style: {
        width: '100%', textAlign: 'left', cursor: 'pointer',
        padding: '11px 14px', borderRadius: 10,
        border: open ? '1px solid #3E7C8C' : '1px solid rgba(62,124,140,0.28)',
        background: '#FFFFFF',
        color: '#102a33',
        fontFamily: "'Outfit',sans-serif", fontSize: 13,
        fontWeight: 700,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        minHeight: 42,
        boxShadow: open ? '0 0 0 3px rgba(62,124,140,0.14)' : 'none',
      }
    },
      React.createElement('span', { className: 'select-value' }, selected ? selected.label : 'Select'),
      React.createElement('span', { className: 'select-caret', style: { fontSize: 11, color: '#2d5f6e' } }, '\u25BC')
    ),
    open && React.createElement('div', {
      className: 'cs-dropdown-list',
      role: 'listbox',
      style: {
        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
        background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
        borderRadius: 10, zIndex: 99999999,
        boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
        overflow: 'hidden',
      }
    },
      options.map(function (o) {
        var isSel = o.value === value;
        return React.createElement('div', {
          key: o.value,
          role: 'option',
          'aria-selected': isSel,
          onClick: function () { onChange(o.value); setOpen(false); },
          style: {
            padding: '10px 14px', fontSize: 13, cursor: 'pointer',
            color: isSel ? '#ffffff' : '#102a33',
            background: isSel ? '#3E7C8C' : '#ffffff',
            fontFamily: "'Outfit',sans-serif",
            fontWeight: 700,
            transition: 'background 0.15s',
          },
          onMouseEnter: function (e) { if (!isSel) e.currentTarget.style.background = '#eef8fa'; },
          onMouseLeave: function (e) { if (!isSel) e.currentTarget.style.background = '#ffffff'; }
        }, o.label);
      })
    )
  );
};

// -- Safe image component --
var CatImage = function (props) {
  var src = resolveImageUrl(props.src || '');
  var alt = props.alt;
  var className = props.className;

  var errState = useState(false);
  var err = errState[0];
  var setErr = errState[1];

  useEffect(function () { setErr(false); }, [src, setErr]);

  if (!src || err || src.includes('example.com')) {
    return React.createElement('span', { className: 'cat-thumb-placeholder' }, 'No image');
  }
  return React.createElement('img', {
    src: src,
    alt: alt || 'category',
    className: className,
    onError: function () { setErr(true); }
  });
};

// -- Toast Notification Component --
var Toast = function (props) {
  var msg = props.msg;
  var onClose = props.onClose;
  if (!msg || !msg.text) return null;

  var isSuccess = msg.type === 's' || msg.type === 'success';

  return React.createElement('div', {
    className: 'cat-toast ' + (isSuccess ? 'cat-toast-success' : 'cat-toast-error'),
    role: 'alert',
    'aria-live': 'assertive'
  },
    React.createElement('div', { className: 'cat-toast-icon' },
      isSuccess
        ? React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
            React.createElement('polyline', { points: '20 6 9 17 4 12' })
          )
        : React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
            React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
            React.createElement('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
            React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
          )
    ),
    React.createElement('span', { className: 'cat-toast-text' }, msg.text),
    React.createElement('button', {
      type: 'button',
      className: 'cat-toast-close',
      onClick: onClose,
      'aria-label': 'Close notification'
    }, React.createElement(XIcon, null))
  );
};

// -- Main Component --
var Category = function () {
  var navigate = useNavigate();
  var categoriesState = useState([]);
  var categories = categoriesState[0];
  var setCategories = categoriesState[1];

  var subcategoriesState = useState(null);
  var subcategories = subcategoriesState[0];
  var setSubcategories = subcategoriesState[1];

  var nameState = useState('');
  var name = nameState[0];
  var setName = nameState[1];

  var descState = useState('');
  var description = descState[0];
  var setDescription = descState[1];

  // -- Display Order state: controls where this category appears (ascending, lower = first) --
  var orderState = useState('');
  var order = orderState[0];
  var setOrder = orderState[1];

  var statusState = useState('active');
  var status = statusState[0];
  var setStatus = statusState[1];

  var imageFileState = useState(null);
  var imageFile = imageFileState[0];
  var setImageFile = imageFileState[1];

  var imagePreviewState = useState('');
  var imagePreview = imagePreviewState[0];
  var setImagePreview = imagePreviewState[1];

  var originalImageState = useState('');
  var originalImage = originalImageState[0];
  var setOriginalImage = originalImageState[1];

  var editIdState = useState(null);
  var editId = editIdState[0];
  var setEditId = editIdState[1];

  var msgState = useState({ text: '', type: '' });
  var msg = msgState[0];
  var setMsg = msgState[1];

  var modalErrorState = useState('');
  var modalError = modalErrorState[0];
  var setModalError = modalErrorState[1];

  var showUpdateSuccessState = useState(false);
  var showUpdateSuccess = showUpdateSuccessState[0];
  var setShowUpdateSuccess = showUpdateSuccessState[1];

  var toastTimerRef = useRef(null);

  var showModalState = useState(false);
  var showModal = showModalState[0];
  var setShowModal = showModalState[1];

  var viewCatState = useState(null);
  var viewCat = viewCatState[0];
  var setViewCat = viewCatState[1];

  var imageRemovedState = useState(false);
  var imageRemoved = imageRemovedState[0];
  var setImageRemoved = imageRemovedState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  // -- Tracks whether the initial categories fetch is still in flight --
  var catsLoadingState = useState(true);
  var catsLoading = catsLoadingState[0];
  var setCatsLoading = catsLoadingState[1];

  // -- Image size warning modal state --
  var showSizeWarnState = useState(false);
  var showSizeWarn = showSizeWarnState[0];
  var setShowSizeWarn = showSizeWarnState[1];

  // -- Image type warning modal state --
  var showTypeWarnState = useState(false);
  var showTypeWarn = showTypeWarnState[0];
  var setShowTypeWarn = showTypeWarnState[1];

  var deleteConfirmState = useState(null);
  var deleteConfirm = deleteConfirmState[0];
  var setDeleteConfirm = deleteConfirmState[1];

  var deleteSuccessState = useState(null);
  var deleteSuccess = deleteSuccessState[0];
  var setDeleteSuccess = deleteSuccessState[1];

  var nameErrorState = useState('');
  var nameError = nameErrorState[0];
  var setNameError = nameErrorState[1];

  var statusFilterState = useState('all');
  var statusFilter = statusFilterState[0];
  var setStatusFilter = statusFilterState[1];

  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var fromDateState = useState('');
  var fromDate = fromDateState[0];
  var setFromDate = fromDateState[1];

  var toDateState = useState('');
  var toDate = toDateState[0];
  var setToDate = toDateState[1];

  var pageState = useState(1);
  var currentPage = pageState[0];
  var setCurrentPage = pageState[1];

  var fileInputRef = useRef();

  // -- fetch --
  var fetchCategories = function () {
    setCatsLoading(true);
    getAllCategories()
      .then(function (res) {
        setCategories(extractList(res));
      })
      .catch(function (err) {
        console.error('Fetch failed:', err);
        setCategories([]);
      })
      .finally(function () {
        setCatsLoading(false);
      });

    getAllSubcategories()
      .then(function (res) { setSubcategories(extractList(res)); })
      .catch(function (err) {
        console.error('Subcategory count fetch failed:', err);
        setSubcategories(null);
      });
  };

  useEffect(function () {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function () {
    return function () {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // -- helpers --
  var flash = function (text, type) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setMsg({ text: text, type: type || 's' });
    toastTimerRef.current = setTimeout(function () {
      setMsg({ text: '', type: '' });
    }, 4000);
  };

  // -- Name validation helper (only A-Z, a-z, 0-9, and spaces) --
  var validateName = function (nameValue) {
    var nameToValidate = nameValue || name;
    var isValid = /^[a-zA-Z0-9\s]*$/.test(nameToValidate);
    if (!isValid) {
      setNameError('Special characters are not allowed');
      return false;
    }
    setNameError('');
    return true;
  };

  var resetFileInput = function () {
    try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { /* noop */ }
  };

  var clearImage = function () {
    setImagePreview('');
    setImageFile(null);
    setImageRemoved(true);
    resetFileInput();
  };

  var closeModal = function () {
    setShowModal(false);
    setEditId(null);
    setName('');
    setDescription('');
    setOrder('');
    setStatus('active');
    clearImage();
    setOriginalImage('');
    setModalError('');
    setNameError('');
    setLoading(false);
    setImageRemoved(false);
  };

  // -- SINGLE validation gate for image pick / drop --
  var validateAndSetImage = function (file) {
    if (!file) return;

// Step 1: must be JPG, PNG, or WEBP
  if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/webp') {
      resetFileInput();
      setShowTypeWarn(true);  // show type warning modal
      return;
    }

    // Step 2: must be <= 1 MB � checked BEFORE anything else
    if (file.size > MAX_IMAGE_BYTES) {
      resetFileInput();       // wipe <input> so browser doesn't cache the bad file
      setShowSizeWarn(true);  // show warning modal � no other state touched
      return;
    }

    // Valid file
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageRemoved(false);
  };

  var handleImageChange = function (e) {
    validateAndSetImage(e.target.files && e.target.files[0]);
  };

  var handleDrop = function (e) {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    validateAndSetImage(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  // -- submit --
  var handleSubmit = async function () {
    if (!name.trim()) { setModalError('Category name is required'); return; }
    if (!validateName(name)) { setModalError('Special characters are not allowed'); return; }

    // -- Order validation: optional, but if provided must be a valid number --
    var trimmedOrder = order.toString().trim();
    if (trimmedOrder !== '' && isNaN(Number(trimmedOrder))) {
      setModalError('Display order must be a number');
      return;
    }

    // Safety net: block submit if file somehow bypassed upload gate
    if (imageFile && imageFile.size > MAX_IMAGE_BYTES) {
      setImageFile(null); setImagePreview(''); resetFileInput();
      setShowSizeWarn(true);
      return;
    }

    setLoading(true);
    setModalError('');

    var onSuccess = function () {
      closeModal();
      if (editId) {
        setShowUpdateSuccess(true);
      } else {
        flash('Category created successfully', 's');
      }
      fetchCategories();
    };

    var onError = function (e) {
      var errMsg = (e.response && e.response.data && e.response.data.msg)
        || (e.response && e.response.data && e.response.data.message)
        || 'Error occurred - check console';
      setModalError(errMsg);
      flash(errMsg, 'e');
      console.error('Submit error:', (e.response && e.response.data) || e.message);
    };

    try {
      var fd = new FormData();
      fd.append('name', name.trim());
      fd.append('description', description.trim());
      fd.append('order', trimmedOrder !== '' ? trimmedOrder : '0');
      fd.append('status', status);
      if (imageRemoved) {
        fd.append('removeImage', 'true');
      } else if (imageFile) {
        fd.append('image', imageFile);
      }
      // If neither: leave the existing image untouched (backend keeps it).

      await (editId ? updateCategory(editId, fd) : createCategory(fd));
      onSuccess();
    } catch (e) {
      onError(e);
    } finally {
      setLoading(false);
    }
  };

  // -- edit --
  var handleEdit = function (cat) {
    setEditId(cat._id);
    setName(cat.name || '');
    setNameError('');
    setModalError('');
    setDescription(cat.description || '');
    setOrder((cat.order !== undefined && cat.order !== null) ? String(cat.order) : '');
    setStatus(cat.status || 'active');
    var rawImg = extractImageRaw(cat) || '';
    setOriginalImage(rawImg);
    setImagePreview(extractImageSrc(cat));
    setImageFile(null);
    setImageRemoved(false);
    setShowModal(true);
  };

  var handleDeleteClick = function (id, catName) {
    setDeleteConfirm({ id: id, name: catName });
  };
  var handleDeleteConfirmed = function () {
    var id = deleteConfirm.id;
    var name = deleteConfirm.name;
    setDeleteConfirm(null);

    if (!id) { flash('Cannot delete: missing ID', 'e'); return; }

    deleteCategory(id)
      .then(function () {
        setDeleteSuccess({ id: id, name: name });
      })
      .catch(function (e) {
        var errMsg = (e.response && e.response.data && e.response.data.msg)
          || (e.response && e.response.data && e.response.data.message)
          || 'Delete failed';
        flash(errMsg, 'e');
        console.error('Delete error:', (e.response && e.response.data) || e.message);
      });
  };

  var handleDeleteSuccessClose = function () {
    var deletedId = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setCategories(function (prev) { return prev.filter(function (c) { return (c._id || c.id) !== deletedId; }); });
    }
    fetchCategories();
  };

  var handleDeleteCancel = function () {
    setDeleteConfirm(null);
  };

  var handleStatCardClick = function (filterVal) {
    setStatusFilter(filterVal);
    setCurrentPage(1);
  };

  // -- Categories shown in the table, sorted by Display Order (ascending) --
  var filteredCategories = sortByOrder(categories).filter(function (c) {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return isActiveStatus(c);
    if (statusFilter === 'inactive') return !isActiveStatus(c);
    return true;
  }).filter(function (c) {
    var query = search.trim().toLowerCase();
    if (query && [c.name, c.description].join(' ').toLowerCase().indexOf(query) === -1) return false;
    var categoryDate = dateInputValue(getCategoryDate(c));
    if (fromDate && (!categoryDate || categoryDate < fromDate)) return false;
    if (toDate && (!categoryDate || categoryDate > toDate)) return false;
    return true;
  });
  var totalPages = Math.max(1, Math.ceil(filteredCategories.length / ITEMS_PER_PAGE));
  var visibleCategories = filteredCategories.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  var subcategoryCount = subcategories === null ? countNestedSubcategories(categories) : subcategories.length;
  var activeCategories = categories.filter(isActiveStatus).length;
  var inactiveCategories = categories.length - activeCategories;
  var categoryStatCards = [
    { title: 'Total Categories', value: categories.length, description: 'All category records', icon: 'grid', accent: '#d99a2b', active: statusFilter === 'all', onClick: function () { setStatusFilter('all'); setCurrentPage(1); } },
    { title: 'Active Categories', value: activeCategories, description: 'Visible in storefront', icon: 'check', accent: '#16a34a', active: statusFilter === 'active', onClick: function () { handleStatCardClick('active'); } },
    { title: 'Inactive Categories', value: inactiveCategories, description: 'Hidden from users', icon: 'pause', accent: '#f59e0b', active: statusFilter === 'inactive', onClick: function () { handleStatCardClick('inactive'); } },
    { title: 'Total Subcategories', value: subcategoryCount, description: 'Linked under categories', icon: 'layers', accent: '#0ea5e9', onClick: function () { navigate('/admin/subcategory'); } },
  ];

  useEffect(function () {
    setCurrentPage(1);
  }, [search, fromDate, toDate, statusFilter]);

  useEffect(function () {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // -- render --
  return React.createElement(AdminLayout, { hideLayoutOnModal: !!(viewCat || showModal || deleteConfirm || showUpdateSuccess || deleteSuccess) },

    // -- Update Success Modal --
    showUpdateSuccess && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: 'Updated successfully!',
      onClose: function () { setShowUpdateSuccess(false); }
    }),

    // -- Floating Toast Notification --
    React.createElement(Toast, {
      msg: msg,
      onClose: function () {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setMsg({ text: '', type: '' });
      }
    }),

    // -- Image Size Warning Modal  rendered FIRST, highest z-index --
    showSizeWarn && React.createElement(ImageSizeWarningModal, {
      onClose: function () { setShowSizeWarn(false); }
    }),

    // -- Image Type Warning Modal --
    showTypeWarn && React.createElement(ImageTypeWarningModal, {
      onClose: function () { setShowTypeWarn(false); }
    }),

    // -- Custom Delete Confirmation Modal --
    deleteConfirm && React.createElement(DeleteConfirmModal, {
      itemName: deleteConfirm.name,
      message: 'Are you sure you want to delete this category?',
      onConfirm: handleDeleteConfirmed,
      onCancel: handleDeleteCancel
    }),

    // -- Delete Success Modal --
    deleteSuccess && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: 'The category has been deleted successfully.',
      onClose: handleDeleteSuccessClose
    }),

    // -- View Modal --
    viewCat && React.createElement('div', {
      className: 'modal-overlay',
      onClick: function (e) { if (e.target.classList.contains('modal-overlay')) setViewCat(null); }
    },
      React.createElement('div', { className: 'view-modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, 'Category Details'),
          React.createElement('button', { className: 'modal-close', onClick: function () { setViewCat(null); } },
            React.createElement(XIcon, null)
          )
        ),
        React.createElement('div', { className: 'view-img-wrap' },
          React.createElement(CatImage, { src: withImageCacheBust(extractImageSrc(viewCat)), alt: viewCat.name, className: 'view-img' })
        ),
        React.createElement('div', { className: 'view-row' },
          React.createElement('span', { className: 'view-label' }, 'Name'),
          React.createElement('span', { className: 'view-val' }, viewCat.name)
        ),
        React.createElement('div', { className: 'view-row' },
          React.createElement('span', { className: 'view-label' }, 'Display Order'),
          React.createElement('span', { className: 'view-val' }, (viewCat.order !== undefined && viewCat.order !== null) ? viewCat.order : '\u2014')
        ),
        React.createElement('div', { className: 'view-row view-full' },
          React.createElement('span', { className: 'view-label' }, 'Description'),
          React.createElement('span', {
            className: 'view-val',
            style: { color: viewCat.description ? '#ffffff' : '#475569' }
          }, viewCat.description || '\u2014')
        ),
        React.createElement('div', { className: 'view-row' },
          React.createElement('span', { className: 'view-label' }, 'Status'),
          React.createElement('span', {
            className: 'bdg ' + (viewCat.status === 'inactive' ? 'bdg-b' : 'bdg-g')
          }, viewCat.status || 'active')
        ),
        React.createElement('div', { className: 'fa' },
          React.createElement('button', { className: 'btn btn-g', onClick: function () { setViewCat(null); } }, 'Close')
        )
      )
    ),

    // -- Add / Edit Modal --
    showModal && React.createElement('div', {
      className: 'modal-overlay',
      onClick: function (e) { if (e.target.classList.contains('modal-overlay')) closeModal(); }
    },
      React.createElement('div', { className: 'modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, editId ? 'Edit Category' : 'Add New Category'),
          React.createElement('button', { className: 'modal-close', onClick: closeModal },
            React.createElement(XIcon, null)
          )
        ),

        modalError && React.createElement('div', {
          className: 'mbx mbx-e'
        }, modalError),

        // Image upload
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Category Image'),
          React.createElement('div', { style: { fontSize: 11, color: '#64748b', marginBottom: 6, fontFamily: "'Outfit',sans-serif" } },
            '\u26A0\uFE0F Max image size: 3.5 MB (PNG, JPG, WEBP)'
          ),
          imagePreview
            ? React.createElement('div', {
                className: 'img-preview-wrap',
                onClick: function () { fileInputRef.current && fileInputRef.current.click(); }
              },
                React.createElement('input', {
                  type: 'file',
                  accept: 'image/jpeg,image/png,image/webp',
                  ref: fileInputRef,
                  style: { display: 'none' },
                  onChange: handleImageChange
                }),
                React.createElement('img', { src: imagePreview, alt: 'preview', className: 'img-preview' }),
                React.createElement('div', {
                  className: 'img-preview-actions cat-img-preview-actions',
                  style: {
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    left: 'auto',
                    bottom: 'auto',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    zIndex: 10,
                    pointerEvents: 'auto'
                  }
                },
                  React.createElement('button', {
                    className: 'img-btn cat-img-change-btn',
                    type: 'button',
                    title: 'Change image',
                    onClick: function (e) { e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); },
                    style: {
                      position: 'static',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 32,
                      padding: '0 16px',
                      borderRadius: 20,
                      background: '#0f172a',
                      color: '#ffffff',
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "'Outfit', sans-serif",
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      boxShadow: '0 3px 10px rgba(15, 23, 42, 0.22)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      margin: 0
                    }
                  }, 'Change'),
                  React.createElement('button', {
                    className: 'img-remove cat-img-remove-btn',
                    type: 'button',
                    title: 'Remove image',
                    onClick: function (e) { e.stopPropagation(); clearImage(); },
                    style: {
                      position: 'static',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      minWidth: 32,
                      maxWidth: 32,
                      height: 32,
                      minHeight: 32,
                      maxHeight: 32,
                      borderRadius: '50%',
                      background: '#ea580c',
                      color: '#ffffff',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 3px 10px rgba(234, 88, 12, 0.28)',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      flexShrink: 0
                    }
                  }, React.createElement(XIcon, null))
                )
              )
            : React.createElement('div', {
                className: 'img-upload-zone',
                onClick: function () { fileInputRef.current && fileInputRef.current.click(); },
                onDragOver: function (e) { e.preventDefault(); e.currentTarget.classList.add('drag-over'); },
                onDragLeave: function (e) { e.currentTarget.classList.remove('drag-over'); },
                onDrop: handleDrop
              },
                React.createElement('input', {
                  type: 'file',
                  accept: 'image/jpeg,image/png,image/webp',
                  ref: fileInputRef,
                  style: { display: 'none' },
                  onChange: handleImageChange
                }),
                React.createElement('span', { style: { color: '#818cf8' } }, React.createElement(UploadIcon, null)),
                React.createElement('span', { className: 'img-upload-hint' },
                  React.createElement('strong', null, 'Click to upload'),
                  ' or drag & drop',
                  React.createElement('br', null),
                  'PNG, JPG, WEBP \u2014 max 3.5 MB'
                )
              )
        ),

        // Name
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null,
            'Category Name *',
            React.createElement('span', {
              className: 'char-count ' + (name.length >= NAME_LIMIT ? 'char-warn' : '')
            }, name.length + '/' + NAME_LIMIT)
          ),
          React.createElement('input', {
            type: 'text',
            placeholder: 'e.g. White Boards',
            value: name,
            maxLength: NAME_LIMIT,
            onChange: function (e) { setName(e.target.value); validateName(e.target.value); },
            style: nameError ? { borderColor: '#ef4444' } : {}
          }),
          nameError && React.createElement('span', {
            style: { color: '#ef4444', fontSize: 13, marginTop: 4, display: 'block', fontFamily: "'Outfit',sans-serif" }
          }, nameError)
        ),

        // Display Order
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Display Order'),
          React.createElement('input', {
            type: 'number',
            placeholder: 'e.g. 1 (lower numbers show first)',
            value: order,
            onChange: function (e) { setOrder(e.target.value); }
          }),
          React.createElement('span', {
            style: { color: '#64748b', fontSize: 11, marginTop: 4, display: 'block', fontFamily: "'Outfit',sans-serif" }
          }, 'Controls the position of this category on the storefront. Lower numbers appear first. Leave blank to default to 0.')
        ),

        // Status
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Status'),
          React.createElement(CustomSelect, {
            value: status,
            onChange: setStatus,
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]
          })
        ),

        // Description
        React.createElement('div', { className: 'fl' },
          React.createElement('label', null,
            'Description (Optional)',
            React.createElement('span', {
              className: 'char-count ' + (description.length >= DESC_LIMIT ? 'char-warn' : '')
            }, description.length + '/' + DESC_LIMIT)
          ),
          React.createElement('textarea', {
            rows: 3,
            placeholder: 'Optional description...',
            value: description,
            maxLength: DESC_LIMIT,
            onChange: function (e) { setDescription(e.target.value); }
          })
        ),

        React.createElement('div', { className: 'fa' },
          React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: closeModal }, 'Cancel'),
          React.createElement('button', {
            className: 'btn btn-p',
            type: 'button',
            disabled: loading,
            style: { opacity: loading ? 0.7 : 1 },
            onClick: handleSubmit
          }, loading ? 'Saving...' : (editId ? 'Update Category' : 'Add Category'))
        )
      )
    ),

    // -- Header --
    React.createElement('div', { className: 'page-header-row' },
      React.createElement('div', { className: 'page-header-main' },
        React.createElement('div', { className: 'page-header-title-row' },
          React.createElement('div', { className: 'page-header-icon' },
            React.createElement('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
              React.createElement('path', { d: 'M7 11c0-2.5 2.5-5.5 5-7 2.5 1.5 5 4.5 5 7a5 5 0 0 1-10 0z' }),
              React.createElement('path', { d: 'M12 4.5c0 2.2-2 4.5-2.5 5.5C9.5 8 10.5 6 12 4.5z' })
            )
          ),
          React.createElement('div', { className: 'ptitle' }, 'Categories')
        ),
        React.createElement('div', { className: 'header-subtitle' }, 'Organize and manage your product categories efficiently')
      ),
      React.createElement('button', {
        className: 'add-btn',
        type: 'button',
        onClick: function () {
          setEditId(null); setName(''); setDescription(''); setOrder(''); setStatus('active');
          clearImage(); setOriginalImage(''); setImageRemoved(false); setShowModal(true);
        }
      },
        React.createElement('svg', { viewBox: '0 0 24 24', style: { width: 15, height: 15, fill: '#fff' } },
          React.createElement('path', { d: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' })
        ),
        ' Add Category'
      )
    ),
    React.createElement(StatCards, { cards: categoryStatCards }),



    // -- Table --
    React.createElement('div', { className: 'wid' },
      React.createElement('div', { className: 'wh' },
        React.createElement('span', { className: 'wt' }, 'Categories (', filteredCategories.length, ' of ', categories.length, ')')
      ),

      React.createElement('div', { className: 'category-controls' },
        React.createElement('div', { className: 'category-search-field' },
          React.createElement('span', { className: 'category-search-icon', 'aria-hidden': 'true' }, '\u2315'),
          React.createElement('input', {
            type: 'text',
            value: search,
            placeholder: 'Search categories...',
            'aria-label': 'Search categories',
            onChange: function (e) { setSearch(e.target.value); }
          })
        ),
        React.createElement('div', { className: 'category-date-item' },
          React.createElement('span', { className: 'category-date-label' }, 'From'),
          React.createElement('input', {
            type: 'date',
            value: fromDate,
            'aria-label': 'Filter categories from date',
            onChange: function (e) { setFromDate(e.target.value); }
          })
        ),
        React.createElement('div', { className: 'category-date-item' },
          React.createElement('span', { className: 'category-date-label' }, 'To'),
          React.createElement('input', {
            type: 'date',
            value: toDate,
            min: fromDate || undefined,
            'aria-label': 'Filter categories to date',
            onChange: function (e) { setToDate(e.target.value); }
          })
        ),
        (search || fromDate || toDate || statusFilter !== 'all') && React.createElement('button', {
          type: 'button',
          className: 'category-clear-btn',
          onClick: function () { setSearch(''); setFromDate(''); setToDate(''); setStatusFilter('all'); setCurrentPage(1); }
        }, 'Clear')
      ),

      React.createElement('table', { className: 'tbl' },
        React.createElement('thead', null,
          React.createElement('tr', null,
            React.createElement('th', null, 'S.No'),
            React.createElement('th', null, 'Image'),
            React.createElement('th', null, 'Name'),
            React.createElement('th', null, 'Order'),
            React.createElement('th', null, 'Description'), 
            React.createElement('th', null, 'Status'),
            React.createElement('th', { className: 'actions-th' }, 'Actions')
          )
        ),
        React.createElement('tbody', null,
          catsLoading
            ? React.createElement('tr', null,
                React.createElement('td', {
                  colSpan: 7,
                  style: { textAlign: 'center', color: '#94a3b8', padding: 32 }
                }, 'Loading categories...')
              )
            : visibleCategories.length === 0
            ? React.createElement('tr', null,
                React.createElement('td', {
                  colSpan: 7,
                  style: { textAlign: 'center', color: '#94a3b8', padding: 32 }
                }, 'No categories found. Add your first one!')
              )
            : visibleCategories.map(function (cat, idx) {
                var catId = cat._id;
                var catName = cat.name;

                return React.createElement('tr', { key: catId },
                  React.createElement('td', { style: { color: '#111827', fontSize: 13, fontWeight: 500 } }, (currentPage - 1) * ITEMS_PER_PAGE + idx + 1),
                  React.createElement('td', null,
                    React.createElement('div', { className: 'cat-thumb' },
                      React.createElement(CatImage, { src: withImageCacheBust(extractImageSrc(cat)), alt: cat.name })
                    )
                  ),
                  React.createElement('td', { style: { fontWeight: 600 } }, cat.name),
                  React.createElement('td', { style: { color: '#111827', fontSize: 13, fontWeight: 500 } }, (cat.order !== undefined && cat.order !== null) ? cat.order : 0),
                  React.createElement('td', { className: 'desc-cell', title: cat.description },
                    cat.description || '\u2014'
                  ),
                  React.createElement('td', null,
                    React.createElement('span', {
                      className: 'bdg ' + (cat.status === 'inactive' ? 'bdg-b' : 'bdg-g')
                    }, cat.status || 'active')
                  ),
                  React.createElement('td', { className: 'actions-cell' },
                    React.createElement('div', { className: 'row-actions' },
                      React.createElement('button', {
                        className: 'row-action-btn row-action-view',
                        title: 'View',
                        'aria-label': 'View',
                        type: 'button',
                        onClick: function (e) { e.stopPropagation(); setViewCat(cat); }
                      }, React.createElement(EyeIcon, null)),
                      React.createElement('button', {
                        className: 'row-action-btn row-action-edit',
                        title: 'Edit',
                        'aria-label': 'Edit',
                        type: 'button',
                        onClick: function (e) { e.stopPropagation(); handleEdit(cat); }
                      }, React.createElement(PencilIcon, null)),
                      React.createElement('button', {
                        className: 'row-action-btn row-action-delete',
                        title: 'Delete',
                        'aria-label': 'Delete',
                        type: 'button',
                        onClick: function (e) { e.stopPropagation(); handleDeleteClick(cat._id, cat.name); }
                      }, React.createElement(TrashIcon, null))
                    )
                  )
                );
              })
        )
      ),
      React.createElement('div', { className: 'category-pagination' },
        React.createElement('span', { className: 'pagination-info' },
          filteredCategories.length === 0 ? 'No matching categories' : 'Showing ',
          filteredCategories.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : '',
          filteredCategories.length > 0 ? '–' + Math.min(currentPage * ITEMS_PER_PAGE, filteredCategories.length) + ' of ' + filteredCategories.length : ''
        ),
        React.createElement('div', { className: 'category-page-buttons' },
          React.createElement('button', {
            type: 'button', disabled: currentPage === 1,
            onClick: function () { setCurrentPage(Math.max(1, currentPage - 1)); }
          }, 'Previous'),
          Array.from({ length: totalPages }, function (_, index) { return index + 1; }).map(function (page) {
            return React.createElement('button', {
              type: 'button', key: page, className: page === currentPage ? 'active' : '',
              onClick: function () { setCurrentPage(page); }
            }, page);
          }),
          React.createElement('button', {
            type: 'button', disabled: currentPage === totalPages,
            onClick: function () { setCurrentPage(Math.min(totalPages, currentPage + 1)); }
          }, 'Next')
        )
      )
    )
  );
};

export default Category;





